import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, CalendarCheck, CheckCircle2, Download, Phone, ShieldCheck, Clock,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Footer from "@/components/landing/Footer";
import { ASSETS, CONTACT } from "@/lib/constants";
import { useRateCheckRouter } from "@/components/RateCheckRouter";
import { setCampaign } from "@/lib/attribution";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAMPAIGN = "annual-rate-check";

const WHY = [
  { h: "Rates move", b: "What was sharp two years ago may not be today — in either direction. Nobody sends you a memo when it changes." },
  { h: "Your home changed value", b: "Equity shifts quietly, and it changes which options are actually on the table for you." },
  { h: "Your life changed", b: "Income, credit, debts, a new job, a kid in college. Pricing follows all of it." },
  { h: "Mortgage insurance", b: "Sometimes it can come off without refinancing at all. Most people never ask." },
  { h: "Programs change", b: "Loan programs come and go constantly. The one that fits you now may not have existed when you closed." },
  { h: "Ten minutes beats three years", b: "The expensive version of this is finding out late. A yearly check is the cheap version." },
];

const STEPS = [
  { n: "01", h: "Two minutes", b: "Tell us where the property is and how to reach you. No documents, no credit pull to get started." },
  { n: "02", h: "A real person", b: "A licensed mortgage professional in your state reviews where your loan stands — and calls you back." },
  { n: "03", h: "A straight answer", b: "Worth doing, or not worth doing. In plain language. Then we check again next year." },
];

const FAQ = [
  { q: "What does it cost?", a: "Nothing. The Annual Rate Check is a review, not a product. There's no fee and no obligation to do anything with what you learn." },
  { q: "Will this hurt my credit?", a: "Getting started doesn't require a credit pull. If you later choose to move forward with an application, your loan officer will explain exactly what's needed and when." },
  { q: "What if there's nothing worth doing?", a: "Then we'll say so plainly — and that's the most common outcome. Knowing you're in good shape is the point of checking." },
  { q: "Am I going to get sales calls?", a: "No. One licensed mortgage professional reaches out, has the conversation, and leaves it with you. Your information isn't sold or passed around." },
  { q: "Who actually does the review?", a: "A licensed mortgage loan originator in your state. All origination activity is performed by licensed professionals — this page just starts the conversation." },
];

export default function AnnualRateCheck() {
  const [season, setSeason] = useState(null);
  const { openRouter } = useRateCheckRouter();
  const params = new URLSearchParams(window.location.search);
  const officerSlug = (params.get("mlo") || "").trim();

  useEffect(() => {
    setCampaign(CAMPAIGN);
    document.title = "The Annual Rate Check · Great Rate Check";
    axios.get(`${API}/annual-check/season`).then((r) => setSeason(r.data.season)).catch(() => {});
  }, []);

  const start = (source) => {
    track("annual_rate_check_start", { source });
    if (officerSlug) {
      window.location.href = `/${officerSlug}/apply`;
      return;
    }
    openRouter(source);
  };

  const headline = season?.headline || "When did you last check your mortgage?";
  const ribbon = !season
    ? null
    : season.open
      ? `${season.label} is open · closes ${season.closes_on} · ${season.days_left} days left`
      : season.phase === "before" && season.enabled
        ? `${season.label} opens ${season.opens_on}`
        : `Run yours any time of year`;

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="annual-rate-check-page">
      <header className="sticky top-0 z-30 bg-linen/90 backdrop-blur border-b border-linen-border">
        <div className="container-grc h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="annual-logo-link">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-ink-600 hover:text-navy"
              data-testid="annual-phone-link"
            >
              <Phone size={14} /> {CONTACT.phoneDisplay}
            </a>
            <button
              onClick={() => start("annual-nav-cta")}
              data-testid="annual-nav-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-lime text-navy-deep font-semibold text-sm px-4 py-2 hover:bg-lime-hover transition-colors"
            >
              Start my check <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(60rem 28rem at 85% -10%, rgba(31,143,139,.55), transparent 60%), radial-gradient(44rem 26rem at -10% 110%, rgba(166,210,74,.3), transparent 60%)",
          }}
        />
        <div className="relative container-grc py-16 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-lime">
              <CalendarCheck size={14} /> The Annual Rate Check
            </div>
            <h1 className="mt-5 font-heading font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
              {headline}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl">
              You check your insurance. You check your phone plan. The biggest payment in your life
              deserves ten minutes once a year. A licensed mortgage professional reviews where your
              loan actually stands and tells you plainly whether anything is worth doing.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={() => start("annual-hero-cta")}
                data-testid="annual-hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-semibold px-7 py-4 hover:bg-lime-hover transition-colors shadow-lg shadow-black/20"
              >
                Start my Annual Rate Check
                <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <span className="text-sm text-white/60">No cost. No obligation. No sales calls.</span>
            </div>
            {ribbon ? (
              <div
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.07] px-4 py-2 text-xs text-white/80"
                data-testid="annual-season-ribbon"
              >
                <Clock size={13} className="text-lime" /> {ribbon}
              </div>
            ) : null}
          </motion.div>
        </div>
      </section>

      {/* Why once a year */}
      <section className="container-grc py-16 sm:py-24" id="why">
        <div className="max-w-2xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal">Why once a year</p>
          <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl text-navy text-balance">
            Six things changed since you closed. You weren't told about any of them.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-linen-border rounded-3xl overflow-hidden border border-linen-border">
          {WHY.map((w, i) => (
            <motion.div
              key={w.h}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="bg-white p-7"
              data-testid={`annual-why-${i}`}
            >
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-3 font-heading font-medium text-lg text-navy">{w.h}</h3>
              <p className="mt-2 text-sm text-ink-500 leading-relaxed">{w.b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-linen-border" id="how">
        <div className="container-grc py-16 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal">How it works</p>
            <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl text-navy">
              Ten minutes, start to answer.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="border-t-2 border-lime pt-6" data-testid={`annual-step-${s.n}`}>
                <div className="font-heading text-3xl text-teal/40">{s.n}</div>
                <h3 className="mt-2 font-heading font-medium text-xl text-navy">{s.h}</h3>
                <p className="mt-2 text-sm text-ink-500 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
          <div
            className="mt-12 flex flex-wrap items-start gap-4 rounded-3xl bg-linen border border-linen-border p-7"
            data-testid="annual-trust-block"
          >
            <ShieldCheck className="text-teal shrink-0 mt-0.5" size={22} />
            <div className="max-w-3xl">
              <h3 className="font-heading font-medium text-lg text-navy">
                "Nothing to change this year" is a real answer — and the most common one.
              </h3>
              <p className="mt-2 text-sm text-ink-500 leading-relaxed">
                This isn't a refinance pitch. It's a yearly review with an honest outcome. If your
                loan is in good shape, you'll hear exactly that, and we'll check again next year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-grc py-16 sm:py-24" id="faq">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal">Questions</p>
            <h2 className="mt-3 font-heading font-medium text-3xl text-navy">The honest answers.</h2>
            <a
              href={`${API}/annual-ratecheck-onepager.pdf`}
              data-testid="annual-onepager-download"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-navy"
            >
              <Download size={15} /> Download the one-pager (PDF)
            </a>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="divide-y divide-linen-border border-y border-linen-border">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`q${i}`} className="border-0">
                  <AccordionTrigger
                    data-testid={`annual-faq-${i}`}
                    className="text-left font-heading font-medium text-base md:text-lg text-navy hover:no-underline py-5"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-ink-500 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy text-white">
        <div className="container-grc py-16 sm:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-lime">
                Got a great rate? Let's check.
              </p>
              <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl text-balance">
                Make it a yearly habit. This year's takes ten minutes.
              </h2>
            </div>
            <button
              onClick={() => start("annual-final-cta")}
              data-testid="annual-final-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-semibold px-7 py-4 hover:bg-lime-hover transition-colors shrink-0"
            >
              Start my Annual Rate Check
              <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-7 gap-y-2 text-xs text-white/55">
            {["No cost", "No obligation", "No credit pull to start", "A licensed professional in your state"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-lime" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
